import { RouteReuseStrategy, DetachedRouteHandle, ActivatedRouteSnapshot } from '@angular/router';

interface StoredRoute {
    handle: DetachedRouteHandle;
    scrollPosition: [number, number];
}

/**
 * Strategi reuse route untuk list views.
 *
 * Route yang memiliki `data: { reuse: true }` akan di-cache saat user navigasi
 * ke detail/form. Saat kembali, component di-reuse dari cache (bukan di-recreate).
 * Data di-refresh otomatis via `reload()` component.
 *
 * Cache di-clear otomatis saat user pindah ke feature berbeda
 * (misal dari `/users` ke `/teams`).
 */
export class ReuseRouteService implements RouteReuseStrategy {
    private storedRoutes = new Map<string, StoredRoute>();
    private currentFeature: string | null = null;

    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        return this.isReusable(route);
    }

    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
        if (!handle || !this.isReusable(route)) return;
        const key = this.buildKey(route);
        this.storedRoutes.set(key, {
            handle,
            scrollPosition: [window.scrollX, window.scrollY],
        });
    }

    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        if (!this.isReusable(route)) return false;
        return this.storedRoutes.has(this.buildKey(route));
    }

    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        if (!this.isReusable(route)) return null;
        const key = this.buildKey(route);
        const stored = this.storedRoutes.get(key);
        if (!stored) return null;

        // Restore scroll position setelah DOM ter-render
        setTimeout(() => {
            window.scrollTo(stored.scrollPosition[0], stored.scrollPosition[1]);
        });

        // Refresh data via reload() — ngOnInit tidak fire saat re-attach dari cache
        const instance = this.getComponentInstance(stored.handle);
        if (instance && typeof (instance as Record<string, unknown>)['reload'] === 'function') {
            setTimeout(() => (instance as { reload(): void })['reload']());
        }

        return stored.handle;
    }

    shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        const futureFeature = this.extractFeature(future);

        // Jika pindah feature, clear semua cache
        if (this.currentFeature && futureFeature !== this.currentFeature) {
            this.storedRoutes.clear();
        }
        this.currentFeature = futureFeature;

        return future.routeConfig === curr.routeConfig;
    }

    /* ================= HELPERS ================= */

    private isReusable(route: ActivatedRouteSnapshot): boolean {
        return (
            !!route.routeConfig &&
            !!route.component &&
            route.children.length === 0 &&
            route.data?.['reuse'] === true
        );
    }

    /**
     * Extract feature name dari URL path pertama setelah root.
     * Misal `/users/123/edit` → `users`
     */
    private extractFeature(route: ActivatedRouteSnapshot): string | null {
        let root = route;
        while (root.parent) {
            root = root.parent;
        }
        const firstChild = root.firstChild;
        if (!firstChild) return null;
        const segment = firstChild.url[0];
        return segment ? segment.path : null;
    }

    /**
     * Build unique key dari full URL path.
     * Misal `/users?page=2&search=john` → `users`
     */
    private buildKey(route: ActivatedRouteSnapshot): string {
        return route.pathFromRoot
            .flatMap((r) => r.url)
            .map((u) => u.path)
            .filter((p) => p)
            .join('/');
    }

    private getComponentInstance(handle: DetachedRouteHandle): Record<string, unknown> | null {
        const ref = handle as { componentRef?: { instance?: Record<string, unknown> } } | null;
        return ref?.componentRef?.instance ?? null;
    }
}
